<ng-container *transloco="let t">
  <div class="mx-auto max-w-[1280px]">
    @if (loading()) {
      <p class="sr-only" role="status">{{ t('userPreferences.loading') }}</p>
      <div class="overflow-hidden rounded-card bg-card shadow-hairline" aria-hidden="true">
        <div class="grid grid-cols-[248px_1fr]">
          <div class="flex flex-col gap-3 border-r border-hairline px-3 py-6">
            <div class="h-3 w-16 animate-pulse rounded bg-surface-2"></div>
            <div class="h-4 w-24 animate-pulse rounded bg-surface-2"></div>
            @for (bone of [0, 1]; track bone) {
              <div class="mt-1 h-10 animate-pulse rounded-lg bg-surface-2"></div>
            }
          </div>
          <div class="flex flex-col gap-6 p-8">
            @for (card of [0, 1]; track card) {
              <div>
                <div class="h-4 w-32 animate-pulse rounded bg-surface-2"></div>
                @for (row of [0, 1]; track row) {
                  <div class="mt-5 flex items-center justify-between gap-6">
                    <div class="h-3.5 w-40 animate-pulse rounded bg-surface-2"></div>
                    <div class="h-8 w-48 animate-pulse rounded-control bg-surface-2"></div>
                  </div>
                }
              </div>
            }
          </div>
        </div>
      </div>
    } @else if (loadFailed()) {
      <div class="rounded-card bg-card p-6 shadow-hairline">
        <p class="text-sm text-ink-2">{{ t('userPreferences.loadFailedBody') }}</p>
        <app-button class="mt-3" size="sm" [label]="t('tenantSettings.retry')" (activated)="retry()" />
      </div>
    } @else {
      <!-- Phone tabs -->
      <nav
        class="mb-3 flex flex-none items-center gap-1 overflow-x-auto border-b border-hairline md:hidden"
        [attr.aria-label]="t('userPreferences.title')"
      >
        @for (id of prefSections; track id) {
          <button
            type="button"
            (click)="goTo(id)"
            class="relative flex-none cursor-pointer whitespace-nowrap px-3 py-2.5 font-display text-[12px] font-bold uppercase leading-4 tracking-[0.03em] transition-colors"
            [class]="activeSection() === id ? 'text-ink-1' : 'text-ink-2 hover:text-ink-1'"
            [attr.aria-current]="activeSection() === id ? 'true' : null"
          >
            {{ t('userPreferences.nav.' + id) }}
            @if (activeSection() === id) {
              <span class="absolute inset-x-1 bottom-0 h-1 bg-action"></span>
            }
          </button>
        }
      </nav>

      <!-- Unified preferences card -->
      <div class="rounded-card bg-card shadow-hairline">
        <div class="grid grid-cols-1 md:grid-cols-[248px_1fr]">

          <!-- Left nav panel -->
          <nav
            class="hidden flex-col gap-7 border-r border-hairline px-3 py-6 md:flex md:sticky md:top-0 md:max-h-screen md:self-start md:overflow-y-auto"
            [attr.aria-label]="t('userPreferences.title')"
          >
            <div class="flex flex-col gap-2 px-3">
              <span class="font-display text-[12px] font-bold uppercase leading-4 tracking-[0.03em] text-ink-2">
                {{ t('userPreferences.personalLabel') }}
              </span>
              <span class="text-[14px] font-semibold leading-5 text-ink-1">{{ userName() }}</span>
            </div>
            <div class="flex flex-col gap-0.5">
              @for (id of prefSections; track id) {
                <button
                  type="button"
                  (click)="goTo(id)"
                  class="flex h-10 cursor-pointer items-center rounded-lg px-3 text-left font-body text-[14px] leading-5 transition-colors"
                  [class]="activeSection() === id
                    ? 'bg-surface-2 font-semibold text-ink-1'
                    : 'font-normal text-ink-1 hover:bg-surface-2'"
                  [attr.aria-current]="activeSection() === id ? 'true' : null"
                >
                  {{ t('userPreferences.nav.' + id) }}
                </button>
              }
            </div>
          </nav>

          <!-- Right scrollable content -->
          <div class="flex min-w-0 flex-col gap-0 divide-y divide-hairline">
            <!-- Page header -->
            <div class="flex items-start gap-4 px-8 py-6">
              <div class="flex flex-col gap-1">
                <h1 class="font-display text-2xl font-bold leading-8 text-ink-1">
                  {{ t('userPreferences.title') }}
                </h1>
                <p class="text-sm leading-5 text-ink-2">{{ t('userPreferences.subtitle', { tenant: tenantName() }) }}</p>
              </div>
            </div>

            <div class="flex flex-col divide-y divide-hairline px-8 pb-16">
              <!-- Regional: USER_OVERRIDABLE, three-layer cascade -->
              <app-setting-section
                id="sec-regional"
                class="scroll-mt-4 py-6"
                [flat]="true"
                [title]="t('userPreferences.regional.title')"
                [subtitle]="t('userPreferences.regional.subtitle')"
                [badge]="t('userPreferences.regional.badge')"
                [saved]="!!savedNotes().regional"
              >
                <app-setting-row
                  [label]="t('tenantSettings.userDefaults.locale.label')"
                  [help]="t('tenantSettings.userDefaults.locale.help')"
                  [defaultLabel]="captions().locale"
                  [showReset]="vm().locale.source === 'USER'"
                  [resetLabel]="t('userPreferences.useTenantDefault')"
                  (reset)="clearPref('locale')"
                >
                  <app-select
                    size="sm"
                    class="block w-60"
                    [ngModel]="vm().locale.value"
                    (ngModelChange)="setPref('locale', $event)"
                    [disabled]="busyKey() === 'locale'"
                    [options]="localeOptions"
                  />
                </app-setting-row>
                <app-setting-row
                  [label]="t('tenantSettings.userDefaults.timezone.label')"
                  [help]="t('tenantSettings.userDefaults.timezone.help')"
                  [defaultLabel]="captions().timezone"
                  [showReset]="vm().timezone.source === 'USER'"
                  [resetLabel]="t('userPreferences.useTenantDefault')"
                  (reset)="clearPref('timezone')"
                >
                  <app-select
                    size="sm"
                    class="block w-60"
                    [ngModel]="vm().timezone.value"
                    (ngModelChange)="setPref('timezone', $event)"
                    [disabled]="busyKey() === 'timezone'"
                    [options]="timezoneOptions"
                  />
                </app-setting-row>
              </app-setting-section>

              <!-- Appearance: USER_ONLY, no tenant layer -->
              <app-setting-section
                id="sec-appearance"
                class="scroll-mt-4 py-6"
                [flat]="true"
                [title]="t('userPreferences.appearance.title')"
                [subtitle]="t('userPreferences.appearance.subtitle')"
                [badge]="t('userPreferences.appearance.badge')"
                [badgeNeutral]="true"
                [saved]="!!savedNotes().appearance"
              >
                <app-setting-row
                  [label]="t('userPreferences.appearance.theme.label')"
                  [defaultLabel]="captions().theme"
                  [showReset]="vm().theme.source === 'USER'"
                  (reset)="clearPref('ui.theme')"
                >
                  <div
                    class="flex gap-1 rounded-pill bg-sunken p-[3px]"
                    role="radiogroup"
                    [attr.aria-label]="t('userPreferences.appearance.theme.label')"
                  >
                    @for (opt of themeOptions; track opt.value) {
                      <button
                        type="button"
                        role="radio"
                        [attr.aria-checked]="vm().theme.value === opt.value"
                        [disabled]="busyKey() === 'ui.theme'"
                        (click)="setTheme(opt.value)"
                        class="h-8 rounded-pill px-4 font-body text-button font-bold transition-colors disabled:cursor-not-allowed disabled:opacity-50"
                        [class]="
                          vm().theme.value === opt.value
                            ? 'bg-card text-ink-1 shadow-hairline'
                            : 'cursor-pointer text-ink-2 hover:text-ink-1'
                        "
                      >
                        {{ t(opt.labelKey) }}
                      </button>
                    }
                  </div>
                </app-setting-row>
                <app-setting-row
                  [label]="t('userPreferences.appearance.page.label')"
                  [help]="t('userPreferences.appearance.page.help')"
                  [defaultLabel]="captions().page"
                  [showReset]="vm().page.source === 'USER'"
                  (reset)="clearPref('ui.defaultPage')"
                >
                  <app-select
                    size="sm"
                    class="block w-60"
                    [ngModel]="vm().page.value"
                    (ngModelChange)="setPref('ui.defaultPage', $event)"
                    [disabled]="busyKey() === 'ui.defaultPage'"
                    [options]="pageOptions()"
                  />
                </app-setting-row>
                <app-setting-row
                  [label]="t('userPreferences.appearance.pageSize.label')"
                  [help]="t('userPreferences.appearance.pageSize.help')"
                  [defaultLabel]="captions().pageSize"
                  [showReset]="vm().pageSize.source === 'USER'"
                  (reset)="clearPref('ui.tablePageSize')"
                >
                  <app-select
                    size="sm"
                    class="block w-44"
                    [ngModel]="vm().pageSize.value"
                    (ngModelChange)="setPref('ui.tablePageSize', $event)"
                    [disabled]="busyKey() === 'ui.tablePageSize'"
                    [options]="pageSizeOptions()"
                  />
                </app-setting-row>
              </app-setting-section>
            </div>
            <div class="h-[70vh]"></div>
          </div>
        </div>
      </div>
    }
  </div>
</ng-container>
